import React, { useState } from 'react';
import { Box, ButtonBase, type SxProps, type Theme } from '@mui/material';

export interface ThumbnailPillProps {
  /** Image URL. Falls back to a plain initial when missing or broken. */
  src?: string | null;
  /** Alt text for the thumbnail. Defaults to empty (decorative). */
  imageAlt?: string;
  /** Primary text. */
  label: string;
  /** Optional secondary text shown under the label. */
  caption?: string;
  onClick?: () => void;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  testId?: string;
}

const THUMBNAIL_SIZE = 28;

/**
 * ThumbnailPill - Pill that leads with a small image and reads as a single chip.
 *
 * Atomic Level: Atom
 *
 * Used for suggestions that benefit from a visual anchor (a place, an area, a
 * recent search). Degrades to the label's first initial when no image loads.
 *
 * @example
 * ```tsx
 * <ThumbnailPill src={area.imageUrl} label="Liberty Village" caption="Neighbourhood" />
 * ```
 */
export function ThumbnailPill({
  src,
  imageAlt = '',
  label,
  caption,
  onClick,
  disabled = false,
  sx,
  testId,
}: ThumbnailPillProps) {
  const [errored, setErrored] = useState(false);

  // Re-show the image when the source changes (adjust-state-on-prop-change).
  const [trackedSrc, setTrackedSrc] = useState(src);
  if (src !== trackedSrc) {
    setTrackedSrc(src);
    setErrored(false);
  }

  const showImage = Boolean(src) && !errored;

  const styles: SxProps<Theme> = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    pl: '4px',
    pr: '12px',
    py: '4px',
    borderRadius: '100px',
    border: '1px solid #d9d9d9',
    backgroundColor: '#fff',
    color: '#1a1a1a',
    textAlign: 'left',
    maxWidth: '100%',
    opacity: disabled ? 0.5 : 1,
    ...(onClick && !disabled ? { '&:hover': { backgroundColor: '#f2f2f2' } } : {}),
    ...sx,
  };

  const content = (
    <>
      <Box
        sx={{
          position: 'relative',
          width: `${THUMBNAIL_SIZE}px`,
          height: `${THUMBNAIL_SIZE}px`,
          flexShrink: 0,
          borderRadius: '50%',
          overflow: 'hidden',
          backgroundColor: '#ececec',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '12px',
          fontWeight: 700,
          color: '#575757',
        }}
      >
        {/*
          A plain img, not next/image: these thumbnails come from hosts that are
          not in next.config.js's image allowlist, and coverage is partial, so
          the onError fallback below has to be able to fire.
        */}
        {showImage ? (
          <Box
            component="img"
            src={src as string}
            alt={imageAlt}
            onError={() => setErrored(true)}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          label.trim().charAt(0).toUpperCase()
        )}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Box
          sx={{
            fontSize: '13px',
            fontWeight: 600,
            lineHeight: 1.3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Box>
        {caption && (
          <Box
            sx={{
              fontSize: '11px',
              lineHeight: 1.3,
              color: '#767676',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {caption}
          </Box>
        )}
      </Box>
    </>
  );

  if (!onClick) {
    return (
      <Box component="span" data-testid={testId} sx={styles}>
        {content}
      </Box>
    );
  }

  return (
    <ButtonBase type="button" onClick={onClick} disabled={disabled} data-testid={testId} sx={styles}>
      {content}
    </ButtonBase>
  );
}

export default ThumbnailPill;
